import { forwardRef, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { HTMLAttributes, MouseEvent, ReactNode } from "react";

type SpotlightCardProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  /** Radius (px) of the cursor-following glow. Default 220. */
  radius?: number;
  /**
   * Glow colour. Defaults to a theme-aware neutral halo that darkens on light
   * surfaces and lightens on dark ones (via `--text-primary`). Pass a brand or
   * status colour to tint the spotlight per card.
   */
  glow?: string;
  /** Disable the hover-lift transform (keep only the spotlight). */
  noLift?: boolean;
  /**
   * Optional floating tooltip shown next to the cursor while hovering. Provide
   * the inner content (a title + rows); the bubble chrome is supplied here.
   */
  tooltip?: ReactNode;
  /**
   * Force the tooltip hidden regardless of hover. Drive this from any overlay
   * opened out of the card (e.g. a dropdown menu's `open` state) so the tooltip
   * doesn't reappear on top of it while the pointer moves over the card.
   */
  suppressTooltip?: boolean;
};

/**
 * A reusable "lift + spotlight" hover surface with an optional cursor-following
 * tooltip. On hover the card raises slightly, a soft radial glow tracks the
 * cursor, and — when `tooltip` is set — a floating bubble follows the pointer.
 *
 * Both the glow and the tooltip position are driven by direct DOM writes on
 * `mousemove` (CSS vars / inline styles), so hovering never re-renders React.
 *
 * Usage: replace a card's root `<div>` with `<SpotlightCard>` and pass the same
 * `className`. Reusable across coupons, orders, product cards, etc.
 */
export const SpotlightCard = forwardRef<HTMLDivElement, SpotlightCardProps>(
  function SpotlightCard(
    {
      children,
      className = "",
      radius = 220,
      glow,
      noLift = false,
      tooltip,
      suppressTooltip = false,
      style,
      ...rest
    },
    ref,
  ) {
    const tipRef = useRef<HTMLDivElement>(null);
    // Whether the pointer is currently over the card.
    const hoveringRef = useRef(false);
    // Once a click happens while hovering (opening a menu/modal), suppress the
    // tooltip until the pointer actually leaves — otherwise moving the cursor
    // back over the still-hovered card would re-show it on top of the overlay.
    const suppressRef = useRef(false);
    // Mirror the controlled `suppressTooltip` prop into a ref so the stable
    // handleMove callback always reads the latest value without re-subscribing.
    // Kept in sync via an effect (writing a ref during render is disallowed).
    const suppressPropRef = useRef(suppressTooltip);

    const handleMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
      hoveringRef.current = true;
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--spot-x", `${e.clientX - rect.left}px`);
      el.style.setProperty("--spot-y", `${e.clientY - rect.top}px`);
      el.style.setProperty("--spot-o", "1");
      const tip = tipRef.current;
      if (tip && !suppressRef.current && !suppressPropRef.current) {
        tip.style.left = `${e.clientX}px`;
        tip.style.top = `${e.clientY}px`;
        tip.style.opacity = "1";
      }
    }, []);

    const handleLeave = useCallback((e: MouseEvent<HTMLDivElement>) => {
      hoveringRef.current = false;
      suppressRef.current = false;
      e.currentTarget.style.setProperty("--spot-o", "0");
      if (tipRef.current) tipRef.current.style.opacity = "0";
    }, []);

    // A menu/modal opening over a stationary cursor fires no mouse event on the
    // card, and libraries like Headless UI stop the trigger's propagation before
    // it reaches the card — so the card's own handlers can't catch it. A
    // capture-phase document listener runs before any of that: on any click that
    // starts while the pointer is over this card, suppress + hide the tooltip
    // (it resumes once the pointer leaves and re-enters).
    const hasTooltip = Boolean(tooltip);
    useEffect(() => {
      if (!hasTooltip) return;
      const onDown = () => {
        if (!hoveringRef.current) return;
        suppressRef.current = true;
        if (tipRef.current) tipRef.current.style.opacity = "0";
      };
      document.addEventListener("pointerdown", onDown, true);
      return () => document.removeEventListener("pointerdown", onDown, true);
    }, [hasTooltip]);

    // Keep the ref in sync and hide instantly when the consumer forces
    // suppression (e.g. its menu opens).
    useEffect(() => {
      suppressPropRef.current = suppressTooltip;
      if (suppressTooltip && tipRef.current) {
        tipRef.current.style.opacity = "0";
      }
    }, [suppressTooltip]);

    return (
      <div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className={[
          "relative transition-[transform,box-shadow] duration-200 ease-out",
          noLift
            ? ""
            : "hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(15,20,40,0.12)]",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        style={style}
        {...rest}
      >
        {children}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-200 motion-reduce:hidden"
          style={{
            opacity: "var(--spot-o, 0)",
            background: `radial-gradient(${radius}px circle at var(--spot-x, 50%) var(--spot-y, 50%), ${
              glow ?? "color-mix(in oklab, var(--text-primary) 7%, transparent)"
            }, transparent 72%)`,
          }}
        />
        {tooltip && typeof document !== "undefined"
          ? createPortal(
              <div
                ref={tipRef}
                aria-hidden="true"
                className="pointer-events-none fixed left-0 top-0 z-[95] min-w-[168px] translate-x-4 translate-y-4 rounded-[12px] border border-[color:var(--card-br)] bg-[color:var(--surface)] px-[13px] py-[11px] text-[color:var(--text-primary)] opacity-0 shadow-[0_12px_30px_rgba(0,0,0,0.22)] transition-opacity duration-150 motion-reduce:hidden"
              >
                {tooltip}
              </div>,
              document.body,
            )
          : null}
      </div>
    );
  },
);
